import {
  CurrentWallboxConfigService,
  RemoteWallboxConfig,
} from '../../../../apps/keba-wallbox-app/src/app/services/current-wallbox-config.service';
import { Injectable } from '@angular/core';
import { LocalClusterService } from './impl/local-cluster.service';
import { RemoteClusterService } from './impl/remote-cluster.service';
import { IClusterService } from './IClusterService';
import { Cluster } from '../../models';
import { sortBy } from 'lodash';
import { SettingsService } from '../settings/settings.service';
import { ConfigurationType, LmgmtKey } from '../../../../apps/keba-wallbox-app/src/plugins/keba-wallbox-plugin';

@Injectable({
  providedIn: 'root',
})
export class ClusterService {
  public constructor(
    private readonly currentWallboxConfigService: CurrentWallboxConfigService,
    private readonly localClusterService: LocalClusterService,
    private readonly remoteClusterService: RemoteClusterService,
    private readonly settingsService: SettingsService
  ) {}

  private get service(): IClusterService {
    const wallboxConfig = this.currentWallboxConfigService.getCurrentWallboxConfig();
    return wallboxConfig?.isLocalWallbox() === true ? this.localClusterService : this.remoteClusterService;
  }

  public readClustersEnabled(): Promise<boolean> {
    let remoteId = this.readRemoteId();
    return this.service.readClustersEnabled(remoteId);
  }

  public writeClustersEnabled(enabled: boolean, wallboxId?: string): Promise<void> {
    let remoteId = wallboxId ?? this.readRemoteId();
    return this.service.writeClustersEnabled(enabled, remoteId);
  }

  public async readClusters(wallboxId?: string): Promise<Cluster[]> {
    let remoteId = wallboxId ?? this.readRemoteId();
    let clusterData = await this.service.readClusters(remoteId);
    return sortBy(clusterData.clusters, ['number']);
  }

  public createCluster(cluster: Cluster, otherClusters?: Cluster[], wallboxId?: string): Promise<void> {
    let remoteId = wallboxId ?? this.readRemoteId();
    return this.service.createCluster(cluster, otherClusters, remoteId);
  }

  public updateCluster(cluster: Cluster, otherClusters?: Cluster[], wallboxId?: string): Promise<void> {
    let remoteId = wallboxId ?? this.readRemoteId();
    return this.service.updateCluster(cluster, otherClusters, remoteId);
  }

  public deleteCluster(cluster: Cluster, otherClusters?: Cluster[], wallboxId?: string): Promise<void> {
    let remoteId = wallboxId ?? this.readRemoteId();
    return this.service.deleteCluster(cluster, otherClusters, remoteId);
  }

  public setClusters(clusters: Cluster[], wallboxId?: string): Promise<void> {
    let remoteId = wallboxId ?? this.readRemoteId();
    return this.service.setClusters(clusters, remoteId);
  }

  public async readMaxCurrent(defaultValue: number, wallboxId?: string): Promise<number> {
    let remoteId = wallboxId ?? this.readRemoteId();
    let maxCurrentSetting = await this.settingsService.readSetting(
      { settingKey: LmgmtKey.MAX_AVAILABLE_CURRENT, configurationType: ConfigurationType.LMGMT },
      remoteId
    );
    if (maxCurrentSetting) {
      return parseInt(maxCurrentSetting.toString());
    }
    return defaultValue;
  }

  private readRemoteId(): string | undefined {
    const wallboxConfig = this.currentWallboxConfigService.getCurrentWallboxConfig();
    if (wallboxConfig?.isRemoteWallbox()) {
      return (wallboxConfig as RemoteWallboxConfig).id;
    }

    return undefined;
  }
}
